import { useQuery } from "@tanstack/react-query";
import { reqFunction } from "utils/API";
import CounterConsumptionChart from "./CounterConsumptionChart";
import GetLocationMap from "components/map/GetLocationMap";
import { OneDeviceObj } from ".";
import { getToday, renderTimeJalali, toPersianDate } from "utils/TimeUtiles";
import { useState } from "react";
import {
  FaTachometerAlt,
  FaCheck,
  FaTint,
  FaTimes,
  FaArrowUp,
  FaArrowDown,
} from "react-icons/fa";
import { WiStrongWind } from "react-icons/wi";
import { Tooltip } from "@chakra-ui/react";

import {
  MdNetworkWifi,
  MdNetworkWifi1Bar,
  MdNetworkWifi2Bar,
  MdNetworkWifi3Bar,
  MdOutlineSignalWifiBad,
  MdPower,
} from "react-icons/md";
import {
  IoBatteryFull,
  IoBatteryDeadOutline,
  IoBatteryHalf,
} from "react-icons/io5";
// import { TiBatteryFull, TiBatteryMid, TiBatteryLow } from "react-icons/ti";

import { FaFaucetDrip, FaFaucet } from "react-icons/fa6";
import {
  // FaTemperatureFull,
  // FaTemperatureHalf,
  FaTemperatureQuarter,
} from "react-icons/fa6";
import moment from "moment-jalaali";

interface Props {
  deviceInfo: [OneDeviceObj];
}

const DeviceVisualDashboard = ({ deviceInfo }: Props) => {
  const [fromDate, setFromDate] = useState(getToday().jDate(1));
  const [tillDate, setTillDate] = useState(getToday());

  const info = deviceInfo[0];

  const [lat, setLat] = useState(
    typeof info?.water_meter_location !== "string" &&
      info?.water_meter_location?.lat
      ? Number(info.water_meter_location.lat)
      : 35.724096
  );
  const [lng, setLng] = useState(
    typeof info?.water_meter_location !== "string" &&
      info?.water_meter_location?.lng
      ? Number(info.water_meter_location.lng)
      : 51.430389
  );

  const operator =
    info?.water_meter_module_info?.water_meter_module_sim_operator === "mtn"
      ? "ایرانسل"
      : info?.water_meter_module_info?.water_meter_module_sim_operator === "mci"
      ? "همراه اول"
      : info?.water_meter_module_info?.water_meter_module_sim_operator ===
        "righttel"
      ? "رایتل"
      : "_";

  const {
    data: dashboardInfo,
    isLoading: dataIsLoading,
    status: dataStatus,
    refetch: dataRefetch,
  } = useQuery({
    queryFn: () =>
      reqFunction(
        "watermeters/admin/getDashboardInfo/consumption",
        {
          water_meter_serial: info?.water_meter_serial,
        },
        "post"
      ),
    queryKey: ["consumptionRecords", info?.water_meter_serial],
  });

  const rawTime = dashboardInfo?.data?.create_time;
  const lastDataAdjustedTime = rawTime
    ? moment(rawTime, "YYYY-M-D HH:mm:ss").add(3, "hours").add(30, "minutes")
    : null;

  const maxRawTime =
    dashboardInfo?.data?.monthly_consumption?.max_consumption?.datetime;
  const MaxDataAdjustedTime = maxRawTime
    ? moment(maxRawTime, "YYYY-M-D HH:mm:ss").add(3, "hours").add(30, "minutes")
    : null;
  const minRawTime =
    dashboardInfo?.data?.monthly_consumption?.min_consumption?.datetime;
  const MinDataAdjustedTime = minRawTime
    ? moment(minRawTime, "YYYY-M-D HH:mm:ss").add(3, "hours").add(30, "minutes")
    : null;

  const moduleCode = info?.water_meter_serial;

  const {
    data: eventsData,
    isLoading: eventsIsLoading,
    status: eventsStatus,
    // refetch: eventsRefetch,
  } = useQuery({
    queryFn: () =>
      reqFunction(
        "Event/admin/getImportant",
        {
          module_id: null,
          module_code: moduleCode ? moduleCode : null,
        },
        "post"
      ),
    queryKey: ["eventsRecords", deviceInfo[0]],
  });

  const {
    data: billsData,
    isLoading: billsIsLoading,
    status: billsStatus,
  } = useQuery({
    queryFn: () =>
      reqFunction("bills/admin/getAll", {
        page: 1,
        count: 1,
        water_meter_serial: info?.water_meter_serial,
        bill_serial: null,
        bill_start_date: null,
        user_id: null,
        user_phone_number: null,
        project_id: null,
        bill_end_date: null,
        payment_dead_line: null,
        bill_create_date: null,
      }),
    queryKey: ["counterBills", info?.water_meter_serial],
  });

  return (
    <div className="flex w-full flex-col gap-4 p-4 text-xs lg:flex-row 4xl:text-xl">
      {/* Right Column */}
      <div className="flex flex-[7] flex-col gap-4">
        {/* Row 1 */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {/* r1i1 - وضعیت کنتور */}
          <div className="relative flex items-center justify-between rounded-xl bg-white p-4 shadow-md dark:bg-navy-700">
            <h3 className="absolute left-1/2 top-3 z-10 -translate-x-1/2 transform bg-white px-2 text-xs font-bold text-navy-700 4xl:text-xl dark:bg-navy-700 dark:text-white">
              وضعیت کنتور
            </h3>

            <div className="mt-5 flex flex-col text-right">
              <h3 className="my-2 text-xs  text-navy-700 4xl:text-xl dark:text-white">
                {info?.water_meter_name}
              </h3>

              {info?.water_meter_activation === 1 ? (
                <p className="text-green-500">فعال</p>
              ) : (
                <p className="text-red-500">غیر فعال</p>
              )}
            </div>

            {info?.water_meter_activation === 1 ? (
              <MeterStatus isActive={true} />
            ) : (
              <MeterStatus isActive={false} />
            )}
          </div>

          {/* r1i2 - رخدادهای مهم */}
          <div className="relative flex flex-col justify-center rounded-xl bg-white p-4 text-center shadow-md dark:bg-navy-700">
            <h3 className="absolute left-1/2 top-3 z-10 -translate-x-1/2 transform text-xs  font-bold text-navy-700 4xl:text-xl dark:text-white">
              رخدادهای مهم
            </h3>
            <div className="mt-5">
              {(eventsData?.data ?? [])
                .filter((event: any) => {
                  const time = event?.event_create_time;
                  if (!time) return false;
                  const eventTime = moment(time, "YYYY-M-D HH:mm:ss")
                    .add(3, "hours")
                    .add(30, "minutes");
                  const now = moment();
                  const diffInDays = now.diff(eventTime, "days");
                  return diffInDays <= 30; // if it was n days ago, don't show it
                })
                .slice(0, 3)
                .map((event: any, index: number) => {
                  const label = event?.event_type?.event_type_keyword ?? "_";
                  const rawTime = event?.event_create_time;

                  const value = rawTime
                    ? moment(rawTime, "YYYY-M-D HH:mm:ss")
                        .add(3, "hours")
                        .add(30, "minutes")
                        .format("jYYYY/jM/jD")
                    : "_";

                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between text-xs  4xl:text-xl"
                    >
                      <span className="text-navy-700 dark:text-white">
                        {label}
                      </span>
                      <span className="text-navy-700 dark:text-white">
                        {value}
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* r1i3 - آخرین ارسال */}
          <div className="relative flex flex-col justify-center rounded-xl bg-white p-4 text-right shadow-md dark:bg-navy-700">
            <h3 className="absolute left-1/2 top-3 z-10 -translate-x-1/2 transform bg-white px-2 text-xs  font-bold text-navy-700 4xl:text-xl dark:bg-navy-700 dark:text-white">
              آخرین ارسال
            </h3>
            <div className="mt-5">
              {[
                {
                  label: "زمان",
                  value: lastDataAdjustedTime?.isValid()
                    ? `${lastDataAdjustedTime.format(
                        "jYYYY/jM/jD"
                      )} - ${lastDataAdjustedTime.format("HH:mm:ss")}`
                    : "_",
                },
                {
                  label: "مصرف (لیتر)",
                  value: dashboardInfo?.data?.value?.toFixed(2) ?? "_",
                },
                {
                  label: "تجمعی (مترمکعب)",
                  value:
                    dashboardInfo?.data?.cumulative_value != null
                      ? dashboardInfo.data.cumulative_value / 1000
                      : "_",
                },
              ].map(({ label, value }, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between text-xs  4xl:text-xl"
                >
                  <span className="text-navy-700 dark:text-white">{label}</span>
                  <span className="text-navy-700 dark:text-white">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {/* r2i1 */}
          <div className="flex flex-col justify-center space-y-4 rounded-xl bg-white p-4 shadow-md dark:bg-navy-700">
            {(() => {
              const signal = dashboardInfo?.data?.information?.SignalQuality;
              const battery =
                dashboardInfo?.data?.information?.voltage_detail?.Battery;
              const backup =
                dashboardInfo?.data?.information?.voltage_detail?.Backup;

              let powerText = "_";
              let mainBatteryText = "_";
              let mainBatteryColor = "text-gray-400";
              let mainBatteryIcon = (
                <IoBatteryDeadOutline
                  className={`text-2xl ${mainBatteryColor}`}
                />
              );
              let secondItemTitle = "منبع تغذیه";

              if (battery >= 3.85) {
                powerText = `${battery}V`;
                secondItemTitle = "منبع تغذیه";
              } else if (battery >= 3.5) {
                mainBatteryText = `${battery}V`;
                mainBatteryColor = "text-green-500";
                mainBatteryIcon = (
                  <IoBatteryFull className={`text-2xl ${mainBatteryColor}`} />
                );
                secondItemTitle = "باتری اصلی";
              } else if (battery >= 3.1) {
                mainBatteryText = `${battery}V`;
                mainBatteryColor = "text-yellow-500";
                mainBatteryIcon = (
                  <IoBatteryHalf className={`text-2xl ${mainBatteryColor}`} />
                );
                secondItemTitle = "باتری اصلی";
              } else if (battery < 3.1 || battery !== undefined) {
                mainBatteryText = `${battery}V`;
                mainBatteryColor = "text-red-500";
                mainBatteryIcon = (
                  <IoBatteryDeadOutline
                    className={`text-2xl ${mainBatteryColor}`}
                  />
                );
                secondItemTitle = "باتری اصلی";
              }

              const items = [
                {
                  title: "کیفیت سیگنال",
                  value: signal !== undefined ? `${signal}%` : "_",
                  icon: (
                    <Tooltip
                      label={`${signal !== 0 ? signal : "__"}`}
                      fontSize="sm"
                    >
                      {signal > 25 ? (
                        <MdNetworkWifi className="text-xl text-green-600" />
                      ) : signal <= 25 && signal > 20 ? (
                        <MdNetworkWifi3Bar className="text-xl text-green-600" />
                      ) : signal <= 20 && signal > 13 ? (
                        <MdNetworkWifi2Bar className="text-xl text-yellow-600" />
                      ) : signal <= 13 && signal !== 0 ? (
                        <MdNetworkWifi1Bar className="text-xl text-red-600" />
                      ) : (
                        <MdOutlineSignalWifiBad className="text-xl" />
                      )}
                    </Tooltip>
                  ),
                },
                {
                  title: secondItemTitle,
                  value:
                    secondItemTitle === "منبع تغذیه"
                      ? powerText
                      : mainBatteryText,
                  icon:
                    secondItemTitle === "منبع تغذیه" ? (
                      <MdPower className="rotate-90 text-2xl text-blue-600" />
                    ) : (
                      mainBatteryIcon
                    ),
                },
                {
                  title: "باتری بکاپ",
                  value: backup !== undefined ? `${backup}V` : "_",
                  icon:
                    backup >= 3.5 ? (
                      <IoBatteryFull className="text-2xl text-green-500" />
                    ) : backup >= 3.1 ? (
                      <IoBatteryHalf className="text-2xl text-yellow-500" />
                    ) : (
                      <IoBatteryDeadOutline className="text-2xl text-red-500" />
                    ),
                },
              ];

              return items.map(({ title, value, icon }, index) => (
                <div
                  key={index}
                  className="flex items-center text-right"
                  style={{ columnGap: "1rem" }}
                >
                  {/* Title column: fixed width */}
                  <div className="w-1/2 text-xs  font-semibold text-navy-700 4xl:text-xl dark:text-white">
                    {title}
                  </div>
                  {/* Value column: fixed width */}
                  <div className="w-1/4 text-center text-xs  text-navy-700 4xl:text-xl dark:text-white">
                    {value}
                  </div>
                  {/* Icon column: fixed width */}
                  <div className="flex w-1/4 justify-center">{icon}</div>
                </div>
              ));
            })()}
          </div>

          {/* r2i2 - وضعیت شیر */}
          <div className="flex items-center justify-between rounded-xl bg-white p-4 shadow-md dark:bg-navy-700">
            <div className="flex flex-col text-right">
              <h3 className="mb-5 text-xs  font-bold text-navy-700 4xl:text-xl dark:text-white">
                وضعیت شیر
              </h3>
              <p className="text-cyan-500">
                {dashboardInfo?.data?.information?.RelayStatus
                  ? "دارد"
                  : "ندارد"}
              </p>
            </div>
            {dashboardInfo?.data?.information?.RelayStatus ? (
              <FaFaucetDrip className="text-4xl text-cyan-500" />
            ) : (
              <FaFaucet className="text-4xl text-cyan-500" />
            )}
          </div>

          {/* r2i3 - دما */}
          <div className="flex items-center justify-between rounded-xl bg-white p-4 shadow-md dark:bg-navy-700">
            <div className="flex flex-1 flex-col text-right">
              {/* Row 1: آب */}
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs  font-bold text-navy-700 4xl:text-xl dark:text-white">
                  دمای آب
                </span>
                <span className="text-blue-500">
                  {dashboardInfo?.data?.information?.temperature_detail?.WT !=
                  null
                    ? `${Math.round(
                        dashboardInfo.data.information.temperature_detail.WT
                      )}°C`
                    : "_"}
                </span>
              </div>

              {/* Row 2: میکرو */}
              <div className="flex items-center justify-between">
                <span className="text-xs  font-bold text-navy-700 4xl:text-xl dark:text-white">
                  دمای میکرو
                </span>
                <span className="text-blue-500">
                  {dashboardInfo?.data?.information?.temperature_detail?.MCU !=
                  null
                    ? `${Math.round(
                        dashboardInfo.data.information.temperature_detail.MCU
                      )}°C`
                    : "_"}
                </span>
              </div>
            </div>

            <FaTemperatureQuarter className="mr-5 text-4xl text-blue-500" />

            {/* {(() => {
              const temp = dashboardInfo?.data?.information?.temperature_detail?.MCU;
              if (typeof temp !== "number")
                return (
                  <FaTemperatureQuarter className="text-4xl text-gray-400" />
                );

              if (temp >= 30) {
                return <FaTemperatureFull className="text-4xl text-red-500" />;
              } else if (temp >= 15) {
                return (
                  <FaTemperatureHalf className="text-4xl text-yellow-500" />
                );
              } else {
                return (
                  <FaTemperatureQuarter className="text-4xl text-blue-500" />
                );
              }
            })()} */}
          </div>

          {/* r2i4 - فشار آب */}
          <div className="flex items-center justify-between rounded-xl bg-white p-4 shadow-md dark:bg-navy-700">
            <div className="flex flex-col text-right">
              <h3 className="mb-5 text-xs  font-bold text-navy-700 4xl:text-xl dark:text-white">
                فشار آب
              </h3>
              <span className="text-blue-500">
                {dashboardInfo?.data?.information?.Pressure != null
                  ? `${Math.max(
                      0,
                      dashboardInfo.data.information.Pressure
                    ).toFixed(2)} bar`
                  : "bar_"}
              </span>
              {/* <p className="text-indigo-500">bar_</p> */}
            </div>
            <div className="flex w-1/3 justify-start">
              <img
                src="/icons/pressure-gauge.png"
                alt="Water Pressure Icon"
                className="h-20 w-20 object-contain"
              />
            </div>
          </div>
        </div>

        {/* Row 3 */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {/* r3i1 */}
          <div className="flex flex-col justify-center space-y-2 rounded-xl bg-white p-4 text-right shadow-md dark:bg-navy-700">
            <h3 className="mb-2 text-center text-xs  font-bold text-navy-700 4xl:text-xl dark:text-white">
              اطلاعات سیم کارت
            </h3>

            {[
              { label: "اپراتور", value: operator },
              {
                label: "شماره سیم کارت",
                value:
                  info?.water_meter_module_info?.water_meter_module_sim ?? "_",
              },
            ].map(({ label, value }, i) => (
              <div
                key={i}
                className="flex justify-between text-xs  4xl:text-xl"
              >
                <span className="text-navy-700 dark:text-white">{label}</span>
                <span className="text-navy-700 dark:text-white">{value}</span>
              </div>
            ))}
          </div>

          {/* r3i2 */}
          <div className="flex flex-col justify-center rounded-xl bg-white p-4 text-right shadow-md dark:bg-navy-700">
            <h3 className="mb-2 text-center text-xs  font-bold text-navy-700 4xl:text-xl dark:text-white">
              اعتبار ریالی و اینترنت
            </h3>
            {[
              { label: "تاریخ", value: "_" },
              { label: "مبلغ", value: "_" },
              { label: "اینترنت", value: "_" },
            ].map(({ label, value }, i) => (
              <div
                key={i}
                className="flex justify-between text-xs  4xl:text-xl"
              >
                <span className="text-navy-700 dark:text-white">{label}</span>
                <span className="text-navy-700 dark:text-white">{value}</span>
              </div>
            ))}
          </div>

          {/* r3i3 */}
          <div className="flex flex-col justify-center space-y-2 rounded-xl bg-white p-4 text-right shadow-md dark:bg-navy-700">
            <div className="flex justify-between text-xs  4xl:text-xl">
              <span className="font-semibold text-navy-700 dark:text-white">
                وضعیت گارانتی
              </span>
              <span className="text-green-500 dark:text-white">_</span>
            </div>
            {[
              { label: "تاریخ شروع", value: "_" },
              { label: "تاریخ پایان", value: "_" },
            ].map(({ label, value }, i) => (
              <div
                key={i}
                className="flex justify-between text-xs  4xl:text-xl"
              >
                <span className="text-navy-700 dark:text-white">{label}</span>
                <span className="text-navy-700 dark:text-white">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Row 4 */}
        <div className="rounded-xl bg-gray-100 p-3 shadow-md dark:bg-navy-700">
          <h3 className="text-center text-xs  font-bold text-navy-700 4xl:text-xl dark:text-white">
            اطلاعات مصرف (لیتر)
          </h3>
        </div>

        {/* Row 5: مصرف بیشینه/کمینه and مصرف متوسط/ماه جاری */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* r5i1 */}
          <div className="flex flex-col justify-center rounded-xl bg-white p-4 text-right shadow-md dark:bg-navy-700">
            <h3 className="mb-2 text-center text-xs  font-bold text-navy-700 4xl:text-xl dark:text-white">
              مصرف میانگین / ماه جاری
            </h3>
            {[
              {
                label: "مصرف متوسط کل",
                value: dashboardInfo?.data?.total_consumption?.avg ?? "_",
              },
              {
                label: "مصرف متوسط ماه جاری",
                value: dashboardInfo?.data?.monthly_consumption?.avg ?? "_",
              },
            ].map(({ label, value }, index) => (
              <div
                key={index}
                className="flex items-center justify-between text-xs  4xl:text-xl"
              >
                <span className="text-navy-700 dark:text-white">{label}</span>
                <span className="text-navy-700 dark:text-white">{value}</span>
              </div>
            ))}
          </div>

          {/* r5i2 */}
          <div className="flex flex-col justify-center rounded-xl bg-white p-4 text-right shadow-md dark:bg-navy-700">
            <h3 className="mb-2 text-center text-xs  font-bold text-navy-700 4xl:text-xl dark:text-white">
              درصد تغییر میانگین مصرف
            </h3>
            {[
              {
                label: "نسبت به ماه قبل",
                percent:
                  dashboardInfo?.data?.monthly_consumption
                    ?.change_vs_previous_month ?? "_",
              },
              {
                label: "نسبت به ماه مشابه سال قبل",
                percent:
                  dashboardInfo?.data?.monthly_consumption
                    ?.change_vs_last_year_same_month ?? "_",
              },
            ].map(({ label, percent }, index) => {
              const isNumber = typeof percent === "number" && !isNaN(percent);

              let content;
              if (isNumber) {
                const isPositive = percent >= 0;
                const ArrowIcon = isPositive ? FaArrowUp : FaArrowDown;
                const color = isPositive ? "text-red-500" : "text-green-500";

                content = (
                  <span
                    className={`flex items-center gap-1 font-medium ${color}`}
                  >
                    {Math.abs(percent)}٪
                    <ArrowIcon className="mb-1 text-xs  4xl:text-xl" />
                  </span>
                );
              } else {
                content = <span className="font-medium">_</span>;
              }

              return (
                <div
                  key={index}
                  className="flex items-center justify-between text-xs  4xl:text-xl"
                >
                  <span className="text-navy-700 dark:text-white">{label}</span>
                  {content}
                </div>
              );
            })}
          </div>
        </div>

        {/* Row 6: درصد تغییر مصرف and قبض */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* r6i1 */}
          <div className="flex flex-col justify-center rounded-xl bg-white p-4 text-right shadow-md dark:bg-navy-700">
            <h3 className="mb-2 text-center text-xs  font-bold text-navy-700 4xl:text-xl dark:text-white">
              مصرف بیشینه / کمینه در ماه جاری
            </h3>
            {[
              {
                label: "مقدار مصرف بیشینه",
                value:
                  dashboardInfo?.data?.monthly_consumption?.max_consumption
                    ?.value ?? "_",
              },
              {
                label: "تاریخ و ساعت",
                value: MaxDataAdjustedTime?.isValid()
                  ? MaxDataAdjustedTime.format("HH:mm:ss jYYYY/jM/jD")
                  : "_",
              },
              {
                label: "مقدار مصرف کمینه",
                value:
                  dashboardInfo?.data?.monthly_consumption?.min_consumption
                    ?.value ?? "_",
              },
              {
                label: "تاریخ و ساعت",
                value: MinDataAdjustedTime?.isValid()
                  ? MinDataAdjustedTime.format("HH:mm:ss jYYYY/jM/jD")
                  : "_",
              },
            ].map(({ label, value }, index) => (
              <div key={index}>
                <div className="flex items-center justify-between text-xs  4xl:text-xl">
                  <span className="text-navy-700 dark:text-white">{label}</span>
                  <span
                    className={`${
                      label === "تاریخ و ساعت"
                        ? "text-gray-500 dark:text-gray-400"
                        : "text-navy-700 dark:text-white"
                    }`}
                  >
                    {value}
                  </span>
                </div>

                {/* Divider after first "تاریخ و ساعت" */}
                {index === 1 && (
                  <div className="my-2 h-px w-full bg-gray-300 dark:bg-gray-600" />
                )}
              </div>
            ))}
          </div>
          {/* r6i2 */}
          <div className="flex flex-col justify-center rounded-xl bg-white p-4 text-right shadow-md dark:bg-navy-700">
            <h3 className="mb-2 text-center text-xs  font-bold text-navy-700 4xl:text-xl dark:text-white">
              آخرین قبض صادر شده
            </h3>
            {[
              {
                label: "مهلت پرداخت",
                value: (() => {
                  const raw = billsData?.data?.[0]?.payment_dead_line;
                  if (!raw) return "_";

                  const m = moment(raw, "YYYY-MM-DD"); // parse as Gregorian date
                  if (!m.isValid()) return "_";

                  return m.format("jYYYY/jM/jD"); // convert to Persian date
                })(),
              },
              {
                label: "مبلغ (ریال)",
                value: billsData?.data[0]?.bill_price ?? "_",
              },
            ].map(({ label, value }, index) => (
              <div
                key={index}
                className="flex items-center justify-between text-xs  4xl:text-xl"
              >
                <span className="text-navy-700 dark:text-white">{label}</span>
                <span className="text-navy-700 dark:text-white">{value}</span>
              </div>
            ))}
            <div className="flex justify-between text-xs  4xl:text-xl">
              <span className="text-navy-700 dark:text-white">وضعیت</span>
              <span className="text-red-500 dark:text-white">
                _{/* در انتظار پرداخت */}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Left Column */}
      <div className="flex flex-[5] flex-col gap-4">
        {/* Consumption Chart */}
        <div className="rounded-xl bg-white p-4 shadow-md dark:bg-navy-700">
          <CounterConsumptionChart
            deviceInfo={deviceInfo}
            fromDate={fromDate}
            setFromDate={null}
            tillDate={tillDate}
            setTillDate={null}
            mode="dashboard"
          />
        </div>

        {/* Location Map */}
        <div className="rounded-xl bg-white p-4 shadow-md dark:bg-navy-700">
          <GetLocationMap
            lat={lat}
            setLat={setLat}
            lng={lng}
            setLng={setLng}
            isInfo={true}
            deviceLoc={
              typeof info?.water_meter_location !== "string" &&
              info?.water_meter_location?.lat
                ? {
                    lat: Number(info.water_meter_location.lat),
                    lng: Number(info.water_meter_location.lng),
                  }
                : null
            }
            mode="dashboard"
          />
        </div>
      </div>
    </div>
  );
};

export default DeviceVisualDashboard;

const MeterStatus = ({ isActive = true }) => {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`flex items-center rounded-full border px-1 py-0.5 transition-all duration-300 ${
          isActive
            ? "border-green-500 bg-green-100"
            : "border-gray-400 bg-gray-100"
        }`}
      >
        <div
          className={`flex h-6 w-6 items-center justify-center rounded-full text-white transition-all duration-300 ${
            isActive ? "bg-green-500" : "bg-gray-400"
          }`}
        >
          {isActive ? <FaCheck /> : <FaTimes />}
        </div>
        <div
          className={`ml-1 mr-3 mt-3 h-6 w-6 rounded-full text-center text-xs  font-bold text-gray-500 4xl:text-xl`}
        >
          {/* Ⅲ */}
        </div>
      </div>
    </div>
  );
};
